import React, { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  Spinner,
  useColorModeValue,
  Button,
  HStack,
  Badge,
  VStack,
} from '@chakra-ui/react';
import Breadcrumbs from './shared/Breadcrumbs';
import fetchWithAuth from '../utils/fetchWithAuth';

function LivePolls() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bgColor = useColorModeValue('background.shade1');
  const pollBoxBgColor = useColorModeValue('background.shade2');

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetchWithAuth('/api/polls/');
      if (!response.ok) {
        throw new Error('Failed to fetch polls');
      }
      const data = await response.json();
      // Filter only live polls
      const livePolls = (data.results || []).filter(poll => poll.state === 'live');
      setPolls(livePolls);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box bg={bgColor} minHeight="100vh">
        <Box maxWidth="900px" margin="0 auto" px={4} py={8}>
          <Breadcrumbs
            items={[
              { label: 'Home', path: '/' },
              { label: 'Live Polls' }
            ]}
          />
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Spinner size="xl" />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg={bgColor} minHeight="100vh">
        <Box maxWidth="900px" margin="0 auto" px={4} py={8}>
          <Breadcrumbs
            items={[
              { label: 'Home', path: '/' },
              { label: 'Live Polls' }
            ]}
          />
          <Box textAlign="center" color="red.500" mt={8}>
            <Text>Error: {error}</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minHeight="100vh">
      <Box maxWidth="900px" margin="0 auto" px={4} py={8}>
        <Breadcrumbs
          items={[
            { label: 'Home', path: '/' },
            { label: 'Live Polls' }
          ]}
        />

        <HStack justify="space-between" mb={6}>
          <Heading as="h1" size="xl">
            Live Polls
          </Heading>
          {polls.length > 0 && (
            <Badge colorScheme="green" fontSize="md" px={3} py={1}>
              {polls.length} Active
            </Badge>
          )}
        </HStack>

        {polls.length === 0 ? (
          <Box
            bg={pollBoxBgColor}
            p={8}
            borderRadius="md"
            border="1px solid"
            borderColor="gray.300"
            textAlign="center"
          >
            <Text color="gray.500" fontSize="lg">
              No live polls. Set a poll live from the <ReactRouterLink to="/stagingpoll" style={{ color: 'teal', textDecoration: 'underline' }}>Stage page</ReactRouterLink>.
            </Text>
          </Box>
        ) : (
          <List spacing={3}>
            {polls.map(poll => (
              <ListItem
                key={poll.id}
                bg={pollBoxBgColor}
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400", shadow: "sm" }}
              >
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between" align="start">
                    <Box flex="1">
                      <Heading as="h3" size="md" mb={1}>
                        {poll.title}
                      </Heading>
                      {poll.description && (
                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                          {poll.description}
                        </Text>
                      )}
                    </Box>
                    <Badge colorScheme="green" fontSize="sm">
                      LIVE
                    </Badge>
                  </HStack>

                  <HStack spacing={2} flexWrap="wrap">
                    <Button
                      as={ReactRouterLink}
                      to={`/live-poll/${poll.id}`}
                      colorScheme="blue"
                      size="sm"
                    >
                      Live Dashboard
                    </Button>
                    <Button
                      as={ReactRouterLink}
                      to={`/analytics/${poll.id}`}
                      colorScheme="purple"
                      size="sm"
                      variant="outline"
                    >
                      Analytics
                    </Button>
                    <Button
                      as={ReactRouterLink}
                      to={`/manage-questions/${poll.id}`}
                      variant="ghost"
                      size="sm"
                    >
                      Manage
                    </Button>
                  </HStack>
                </VStack>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
}

export default LivePolls;
